export type OwnerStepId = 'account' | 'payouts' | 'listing' | 'availability';
export type OwnerStepState = 'done' | 'current' | 'todo';

export interface OwnerProgressListing {
  _id: string;
  title?: string;
  windowCount?: number;
}

export interface OwnerProgressInput {
  isAuthenticated: boolean;
  connect: { connected: boolean; onboarded: boolean } | null;
  listings: OwnerProgressListing[] | null;
}

export interface OwnerStep {
  id: OwnerStepId;
  title: string;
  description: string;
  state: OwnerStepState;
}

export type OwnerCtaKind = 'signup' | 'stripe' | 'link';

export interface OwnerCta {
  label: string;
  kind: OwnerCtaKind;
  /** Only set when kind is 'link'. */
  to?: string;
}

export interface OwnerProgress {
  steps: OwnerStep[];
  completedCount: number;
  isComplete: boolean;
  cta: OwnerCta;
}

export const OWNER_STEP_COPY: Array<{ id: OwnerStepId; title: string; description: string }> = [
  {
    id: 'account',
    title: 'Create your account',
    description: 'Free, and takes about a minute.',
  },
  {
    id: 'payouts',
    title: 'Connect payouts',
    description: 'Stripe verifies you and sends money to your bank.',
  },
  {
    id: 'listing',
    title: 'Create your first listing',
    description: 'Photos, a description, and your price.',
  },
  {
    id: 'availability',
    title: 'Open your availability',
    description: 'Pick the days and times people can book.',
  },
];

export function deriveOwnerProgress(input: OwnerProgressInput): OwnerProgress {
  const listings = input.listings ?? [];
  const connect = input.connect ?? { connected: false, onboarded: false };
  const listingWithoutWindows = listings.find(listing => (listing.windowCount ?? 0) === 0);

  const raw = [
    input.isAuthenticated,
    Boolean(connect.onboarded),
    listings.length > 0,
    listings.some(listing => (listing.windowCount ?? 0) > 0),
  ];

  let stillDone = true;
  const done = raw.map(value => (stillDone = stillDone && value));
  const firstIncomplete = done.findIndex(value => !value);
  const completedCount = done.filter(Boolean).length;
  const isComplete = completedCount === done.length;

  const steps: OwnerStep[] = OWNER_STEP_COPY.map((copy, index) => {
    let state: OwnerStepState = 'todo';
    if (done[index]) {
      state = 'done';
    } else if (index === firstIncomplete) {
      state = 'current';
    }
    return { ...copy, state };
  });

  let cta: OwnerCta;
  if (!input.isAuthenticated) {
    cta = { label: 'Get started free', kind: 'signup' };
  } else if (connect.connected && !connect.onboarded) {
    cta = { label: 'Finish payout setup', kind: 'stripe' };
  } else if (!connect.onboarded) {
    cta = { label: 'Set up payouts', kind: 'stripe' };
  } else if (listings.length === 0) {
    cta = { label: 'Create your first listing', kind: 'link', to: '/listings/create' };
  } else if (listingWithoutWindows) {
    cta = {
      label: 'Add availability',
      kind: 'link',
      to: `/listings/${listingWithoutWindows._id}/windows`,
    };
  } else {
    cta = { label: 'Go to my listings', kind: 'link', to: '/my-listings' };
  }

  return { steps, completedCount, isComplete, cta };
}
